/**
 * Inactivity Warning Dialog
 * Displays a countdown warning when user has been inactive in voice mode
 * This is an unpublished work containing 3M confidential and proprietary information.
 * Disclosure, use or reproduction without the written authorization of 3M is prohibited.
 * If publication occurs, the following notice applies:
 * Copyright (C) 2025, 3M. All rights reserved.
 */

import React from 'react';
import { createPortal } from 'react-dom';
import { useTheme } from '../Theme/ThemeProvider';

interface InactivityWarningDialogProps {
  isOpen: boolean;
  countdown: number; // seconds remaining
  disconnectedByInactivity?: boolean; // true when session ended due to timeout
  onDismiss: () => void;
}

export function InactivityWarningDialog({ 
  isOpen, 
  countdown,
  disconnectedByInactivity = false,
  onDismiss
}: InactivityWarningDialogProps) {
  const { mode } = useTheme();

  if (!isOpen) return null;

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    if (mins > 0) {
      return `${mins}:${secs.toString().padStart(2, '0')}`;
    }
    return `${secs}s`;
  };

  return createPortal(
    <div 
      key="inactivity-warning-dialog"
      className="fixed inset-0 flex items-center justify-center"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)', zIndex: 50000, pointerEvents: 'auto' }}
      onClick={onDismiss}
    >
      <div 
        className="rounded-lg shadow-xl p-6 max-w-md w-full mx-4"
        style={{
          backgroundColor: mode === 'dark' ? '#1a1a1a' : '#ffffff',
          border: `1px solid ${mode === 'dark' ? '#333' : '#e5e5e5'}`
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Title */}
        <h2 
          className="text-lg font-semibold mb-3"
          style={{ 
            color: 'var(--color-text)',
            fontFamily: '3MCircularTT'
          }}
        >
          {disconnectedByInactivity ? "Disconnected from Voice Mode" : "🎙️ Voice Mode Inactivity"}
        </h2>

        {/* Message */}
        <p 
          className="text-sm mb-4"
          style={{ 
            color: 'var(--color-text)',
            fontFamily: '3MCircularTT',
            opacity: 0.9
          }}
        >
          {disconnectedByInactivity 
            ? "You have been disconnected from voice mode due to inactivity." 
            : "No speech has been detected. The chat has been paused. Click the microphone button to unpause and continue chatting. If you don't speak before the timer runs out, the voice mode session will end automatically."}
        </p>

        {/* Countdown Timer */}
        {disconnectedByInactivity ? null : 
        <div 
          className="mb-6 p-4 rounded-lg text-center"
          style={{
            backgroundColor: mode === 'dark' ? '#2a2a2a' : '#f9fafb',
            border: `1px solid ${mode === 'dark' ? '#444' : '#e5e7eb'}`
          }}
        >
          <div 
            className="text-3xl font-bold mb-1"
            style={{ 
              color: countdown <= 10 ? '#ef4444' : 'var(--color-text)',
              fontFamily: '3MCircularTT'
            }}
          >
            {formatTime(countdown)}
          </div>
          <div 
            className="text-xs"
            style={{ 
              color: 'var(--color-text)',
              opacity: 0.7,
              fontFamily: '3MCircularTT'
            }}
          >
            Time remaining
          </div>
        </div>
    }

        {/* Info text */}
        {disconnectedByInactivity ? null :
        <p 
          className="text-xs mb-4"
          style={{ 
            color: 'var(--color-text)',
            fontFamily: '3MCircularTT',
            opacity: 0.7,
            textAlign: 'center'
          }}
        >
        Please speak to continue your session. You can dismiss this message, but the countdown will continue until you speak.
        </p>
}

        {/* Dismiss Button */}
        <div className="flex justify-center">
          <button
            onClick={onDismiss}
            className="px-5 py-2 rounded text-sm font-medium transition-colors"
            style={{
              backgroundColor: 'var(--color-accent)',
              color: '#ffffff',
              fontFamily: '3MCircularTT',
              border: 'none',
              cursor: 'pointer'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.opacity = '0.9';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.opacity = '1';
            }}
          >
            Dismiss
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
